/**
 * Quiz bank.
 *
 *   id          stable key — answers are persisted against it
 *   question    prompt shown to the player
 *   options     exactly four choices
 *   answer      index into `options`
 *   explanation shown after answering, right or wrong
 *   difficulty  'easy' | 'medium' | 'hard' — drives XP and ordering
 *   category    used for the per-category breakdown on the home screen
 */

export const QUESTIONS = [
  {
    id: 'q-gen-open',
    question:
      'Which book opens with “In the beginning, God created the heavens and the earth”?',
    options: ['Genesis', 'Exodus', 'John', 'Psalms'],
    answer: 0,
    explanation:
      'Genesis 1:1 is the Bible’s first verse. John 1:1 echoes it — “In the beginning was the Word.”',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-psa-23-shepherd',
    question: 'Complete Psalm 23:1 — “The LORD is my ___; I shall lack nothing.”',
    options: ['shepherd', 'refuge', 'fortress', 'light'],
    answer: 0,
    explanation:
      'Psalm 23 is David’s shepherd psalm — fitting, since he was a shepherd before he was king.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-jos-courage',
    question:
      'Who was told, “Be strong and courageous… for the LORD your God is with you wherever you go”?',
    options: ['Joshua', 'Moses', 'David', 'Daniel'],
    answer: 0,
    explanation:
      'Joshua 1:9, spoken as Joshua took over from Moses to lead Israel into the promised land.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-joh-3-16',
    question: 'According to John 3:16, what did God give because he so loved the world?',
    options: [
      'His one and only Son',
      'The Ten Commandments',
      'The promised land',
      'The Holy Spirit',
    ],
    answer: 0,
    explanation:
      '“…that whoever believes in him should not perish, but have eternal life.”',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-eph-grace',
    question: 'According to Ephesians 2:8–9, how are we saved?',
    options: [
      'By grace, through faith',
      'By keeping the law',
      'By good works',
      'By sacrifices',
    ],
    answer: 0,
    explanation:
      '“…and that not of yourselves; it is the gift of God, not of works, that no one would boast.”',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-book-count',
    question: 'How many books are in the Protestant Bible?',
    options: ['66', '72', '39', '27'],
    answer: 0,
    explanation: '39 in the Old Testament plus 27 in the New Testament.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-ot-count',
    question: 'How many books are in the Old Testament?',
    options: ['39', '27', '24', '46'],
    answer: 0,
    explanation:
      'The same writings are counted as 24 books in the Hebrew Bible, grouped differently.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-joh-word',
    question: 'Which Gospel begins “In the beginning was the Word”?',
    options: ['John', 'Matthew', 'Mark', 'Luke'],
    answer: 0,
    explanation:
      'John opens outside of time rather than with a genealogy or a birth narrative.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-psalms-author',
    question: 'Who is traditionally credited with writing most of the Psalms?',
    options: ['David', 'Solomon', 'Moses', 'Isaiah'],
    answer: 0,
    explanation:
      'Roughly half the psalms carry a Davidic superscription; others are by Asaph, the sons of Korah, Moses and more.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-pro-trust',
    question: '“Trust in the LORD with all your heart” comes from which book?',
    options: ['Proverbs', 'Psalms', 'Isaiah', 'James'],
    answer: 0,
    explanation:
      'Proverbs 3:5–6 — “…and don’t lean on your own understanding.”',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-gospels',
    question: 'What are the first four books of the New Testament called?',
    options: ['The Gospels', 'The Epistles', 'The Pentateuch', 'The Prophets'],
    answer: 0,
    explanation:
      'Matthew, Mark, Luke and John. The Pentateuch is the first five books of the Old Testament.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-fruit-first',
    question: 'The fruit of the Spirit in Galatians 5:22 begins with which quality?',
    options: ['Love', 'Joy', 'Peace', 'Patience'],
    answer: 0,
    explanation: 'Love, joy, peace, patience, kindness, goodness, faith, gentleness, self-control.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-lions-den',
    question: 'Who was thrown into a den of lions and survived?',
    options: ['Daniel', 'David', 'Samson', 'Elijah'],
    answer: 0,
    explanation:
      'Daniel 6 — he kept praying at his window even after the king’s decree made it illegal.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-ark',
    question: 'Who built the ark before the flood?',
    options: ['Noah', 'Abraham', 'Enoch', 'Methuselah'],
    answer: 0,
    explanation: 'Genesis 6–9. The rainbow is given as the sign of the covenant afterwards.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-sinai',
    question: 'On which mountain did Moses receive the Ten Commandments?',
    options: ['Mount Sinai', 'Mount Carmel', 'Mount Zion', 'Mount Ararat'],
    answer: 0,
    explanation:
      'Exodus 19–20. Mount Carmel is where Elijah confronted the prophets of Baal.',
    difficulty: 'medium',
    category: 'Places',
  },
  {
    id: 'q-phi-4-13',
    question: '“I can do all things through Christ, who strengthens me” is from which letter?',
    options: ['Philippians', 'Romans', 'Colossians', 'Hebrews'],
    answer: 0,
    explanation:
      'Philippians 4:13 — written by Paul from prison, in the context of learning contentment.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-peter-denial',
    question: 'Which disciple denied Jesus three times before the rooster crowed?',
    options: ['Peter', 'Thomas', 'Judas', 'John'],
    answer: 0,
    explanation:
      'All four Gospels record it — and John 21 records Jesus restoring Peter with three questions.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-twelve',
    question: 'How many disciples did Jesus choose as apostles?',
    options: ['Twelve', 'Seven', 'Seventy', 'Three'],
    answer: 0,
    explanation:
      'Twelve, matching the twelve tribes of Israel. Luke 10 also records seventy sent out.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-last-book',
    question: 'Which book comes last in the Bible?',
    options: ['Revelation', 'Jude', 'Malachi', '3 John'],
    answer: 0,
    explanation: 'Malachi is last in the Old Testament; Revelation closes the whole canon.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-first-commandment',
    question: 'What is the first of the Ten Commandments (Exodus 20:3)?',
    options: [
      'You shall have no other gods before me',
      'You shall not murder',
      'Honour your father and mother',
      'Remember the Sabbath day',
    ],
    answer: 0,
    explanation: 'The commandments begin with allegiance to God before turning to conduct.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-rom-6-23',
    question: 'Complete Romans 6:23 — “For the wages of sin is ___.”',
    options: ['death', 'sorrow', 'exile', 'judgement'],
    answer: 0,
    explanation:
      '“…but the free gift of God is eternal life in Christ Jesus our Lord.”',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-mary',
    question: 'Who was the mother of Jesus?',
    options: ['Mary', 'Martha', 'Elizabeth', 'Anna'],
    answer: 0,
    explanation:
      'Elizabeth was her relative and the mother of John the Baptist (Luke 1).',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-jonah',
    question: 'Which prophet was swallowed by a great fish?',
    options: ['Jonah', 'Amos', 'Hosea', 'Nahum'],
    answer: 0,
    explanation:
      'Jonah 1–2, while fleeing from God’s call to preach to Nineveh.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-love-chapter',
    question: '“Love is patient and is kind” appears in which chapter?',
    options: ['1 Corinthians 13', 'Romans 12', '1 John 4', 'Ephesians 5'],
    answer: 0,
    explanation:
      '1 Corinthians 13 — often called “the love chapter”, written to a badly divided church.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-first-king',
    question: 'Who was the first king of Israel?',
    options: ['Saul', 'David', 'Solomon', 'Samuel'],
    answer: 0,
    explanation:
      'Samuel was the prophet who anointed him — and later anointed David (1 Samuel 9–16).',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-exodus-book',
    question: 'Which book records Israel’s escape from slavery in Egypt?',
    options: ['Exodus', 'Genesis', 'Numbers', 'Judges'],
    answer: 0,
    explanation:
      '“Exodus” means “going out”. Numbers covers the wilderness wandering that follows.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-cana',
    question: 'In which town did Jesus turn water into wine?',
    options: ['Cana', 'Capernaum', 'Bethany', 'Nazareth'],
    answer: 0,
    explanation:
      'John 2 calls it the first of his signs, performed at a wedding in Cana of Galilee.',
    difficulty: 'hard',
    category: 'Places',
  },
  {
    id: 'q-mic-6-8',
    question:
      'Micah 6:8 — “act justly, love mercy, and walk ___ with your God.”',
    options: ['humbly', 'boldly', 'gladly', 'daily'],
    answer: 0,
    explanation:
      'A summary of what God requires, given against a backdrop of empty religious ritual.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-isa-40-31',
    question:
      'Isaiah 40:31 — those who wait for the LORD “will mount up with wings like ___.”',
    options: ['eagles', 'doves', 'ravens', 'angels'],
    answer: 0,
    explanation: '“They will run, and not be weary. They will walk, and not faint.”',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-baptist',
    question: 'Who baptised Jesus in the Jordan?',
    options: ['John the Baptist', 'Peter', 'Elijah', 'Andrew'],
    answer: 0,
    explanation:
      'Matthew 3. John protested that he should be baptised by Jesus instead.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-galatians-fruit',
    question: 'Which of Paul’s letters lists the fruit of the Spirit?',
    options: ['Galatians', 'Ephesians', 'Philippians', 'Titus'],
    answer: 0,
    explanation: 'Galatians 5:22–23, contrasted with “the works of the flesh”.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-heb-11-1',
    question: 'Hebrews 11:1 defines faith as assurance of things ___ for.',
    options: ['hoped', 'longed', 'asked', 'prayed'],
    answer: 0,
    explanation:
      '“…proof of things not seen.” The chapter then lists the heroes of faith.',
    difficulty: 'hard',
    category: 'Verses',
  },
  {
    id: 'q-ruth',
    question:
      'Who said, “Your people will be my people, and your God my God”?',
    options: ['Ruth', 'Esther', 'Hannah', 'Rahab'],
    answer: 0,
    explanation:
      'Ruth 1:16, spoken to her mother-in-law Naomi. Ruth becomes an ancestor of David.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-joseph',
    question:
      'Who was sold into slavery by his brothers but later saved them from famine?',
    options: ['Joseph', 'Jacob', 'Benjamin', 'Judah'],
    answer: 0,
    explanation:
      'Genesis 37–50, ending with “you meant evil against me, but God meant it for good”.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-psa-119-105',
    question: 'Psalm 119:105 — “Your word is a ___ to my feet, and a light for my path.”',
    options: ['lamp', 'guide', 'map', 'staff'],
    answer: 0,
    explanation:
      'Psalm 119 is the longest chapter in the Bible, and nearly every verse praises God’s word.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-gospel-meaning',
    question: 'What does the word “gospel” mean?',
    options: ['Good news', 'Holy book', 'Teaching', 'Covenant'],
    answer: 0,
    explanation:
      'From the Old English “gōdspel”, translating the Greek *euangelion* — good news.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-gen-50-20',
    question: 'Genesis 50:20 — “You meant evil against me, but God meant it for ___.”',
    options: ['good', 'glory', 'justice', 'mercy'],
    answer: 0,
    explanation: '“…to save many people alive, as is happening today.”',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-romans-author',
    question: 'Who wrote the letter to the Romans?',
    options: ['Paul', 'Peter', 'James', 'Luke'],
    answer: 0,
    explanation:
      'Paul, writing ahead of a visit he hoped to make on the way to Spain (Romans 15).',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-jer-29-11',
    question:
      'Jeremiah 29:11 — God’s thoughts towards his people are “thoughts of peace, and not of ___.”',
    options: ['evil', 'anger', 'sorrow', 'exile'],
    answer: 0,
    explanation:
      'Spoken to exiles in Babylon who were told to settle there for seventy years first.',
    difficulty: 'hard',
    category: 'Verses',
  },
  {
    id: 'q-rev-21-4',
    question: 'Revelation 21:4 — God will wipe away every ___ from their eyes.',
    options: ['tear', 'shadow', 'fear', 'stain'],
    answer: 0,
    explanation: '“Death will be no more; neither will there be mourning, nor crying, nor pain.”',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-solomon',
    question: 'Which king was famous for his wisdom and wrote much of Proverbs?',
    options: ['Solomon', 'Hezekiah', 'Josiah', 'Rehoboam'],
    answer: 0,
    explanation:
      'Solomon, David’s son, who asked God for a discerning heart rather than riches (1 Kings 3).',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-after-gospels',
    question: 'Which book comes immediately after the four Gospels?',
    options: ['Acts', 'Romans', 'Revelation', 'Hebrews'],
    answer: 0,
    explanation:
      'Acts, written by Luke as a sequel to his Gospel, tells the story of the early church.',
    difficulty: 'easy',
    category: 'Books',
  },

  // ── Expansion ──────────────────────────────────────────────────────────
  // Broader coverage so a returning player keeps meeting new material rather
  // than the same forty questions. Practice selection retires anything
  // answered correctly twice, which only works if the bank is deep enough.
  {
    id: 'q-pentateuch-name',
    question:
      'The first five books of the Bible — Genesis through Deuteronomy — are known collectively by which name?',
    options: ['The Pentateuch', 'The Prophets', 'The Writings', 'The Historical Books'],
    answer: 0,
    explanation:
      '‘Pentateuch’ comes from the Greek for ‘five scrolls’; Jewish tradition calls the same five books the Torah, meaning instruction. Everything that follows leans on them — the law, the covenant and the exodus story are the framework later writers keep returning to.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-after-deuteronomy',
    question:
      'Which book opens immediately after Deuteronomy, carrying Israel across the Jordan into Canaan?',
    options: ['Joshua', 'Judges', 'Numbers', '1 Samuel'],
    answer: 0,
    explanation:
      'The narrative baton passes at Moses\' death to Joshua, his lieutenant. Judges then covers the unsettled generations that follow, before Israel asks for a king.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-synoptic-three',
    question:
      'Three of the four Gospels are called ‘Synoptic\' because they tell Jesus\' story along broadly similar lines. Which three?',
    options: ['Matthew, Mark and Luke', 'Mark, Luke and John', 'Matthew, Mark and John', 'Matthew, Luke and John'],
    answer: 0,
    explanation:
      '‘Synoptic\' means ‘seen together\' — set side by side in columns, the three share a great deal of wording and order. John takes a different route, built around long discourses and a series of signs.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-ot-last-book',
    question:
      'Which book stands last in the Christian Old Testament?',
    options: ['Malachi', 'Zechariah', 'Nehemiah', 'Daniel'],
    answer: 0,
    explanation:
      'Malachi\'s closing promise of a coming messenger makes a tidy hinge into the Gospels — but that is a Christian arrangement. The Hebrew Bible orders the same books differently and ends with Chronicles.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-most-chapters',
    question:
      'Which book contains more chapters than any other in the Bible?',
    options: ['Psalms', 'Isaiah', 'Genesis', 'Jeremiah'],
    answer: 0,
    explanation:
      'Psalms runs to 150, though its ‘chapters\' are really separate poems gathered over centuries. The longest, Psalm 119, is an acrostic of 176 verses arranged by the Hebrew alphabet.',
    difficulty: 'easy',
    category: 'Books',
  },
  {
    id: 'q-philemon-onesimus',
    question:
      'Which of Paul\'s letters is a personal appeal made on behalf of a slave named Onesimus?',
    options: ['Philemon', 'Colossians', 'Galatians', 'Ephesians'],
    answer: 0,
    explanation:
      'At 25 verses it is the shortest thing Paul wrote, and the only letter turning on a single domestic matter. Onesimus is greeted by name in Colossians too, which suggests the two letters travelled together.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-hebrews-anonymous',
    question:
      'Which of these New Testament letters nowhere names the person who wrote it?',
    options: ['Hebrews', 'James', 'Jude', '1 Peter'],
    answer: 0,
    explanation:
      'Hebrews begins not with a greeting but with a sentence about how God has spoken, and it never signs off with a name. It is not quite alone in that: 1 John names no author either, and 2 and 3 John come only from ‘the elder’. Readers ancient and modern have proposed Paul, Barnabas, Apollos and Priscilla for Hebrews; the book itself keeps its silence, calling what it offers a ‘word of exhortation’.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-esther-no-god',
    question:
      'Which Old Testament book never mentions God by name?',
    options: ['Esther', 'Ruth', 'Lamentations', 'Nehemiah'],
    answer: 0,
    explanation:
      'Esther tells of a Jewish queen saving her people in the Persian court, with providence working entirely offstage. The longer Greek version, kept in Catholic and Orthodox Bibles, adds prayers that do name God.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-minor-prophets-why',
    question:
      'The twelve Minor Prophets are called ‘minor\' for what reason?',
    options: ['Their books are much shorter than the others', 'They prophesied for much shorter periods', 'They ranked below the priests of the temple', 'They addressed foreign nations, not Israel'],
    answer: 0,
    explanation:
      'Length alone, not importance: together the twelve fitted on a single scroll, and Jewish tradition still counts them as one book, ‘The Twelve\'. Hosea, Amos and Micah were preaching long before the ‘major\' prophet Jeremiah was born.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-luke-acts-volumes',
    question:
      'Which two New Testament books form a single work in two volumes, each addressed to a man named Theophilus?',
    options: ['Luke and Acts', 'John and Revelation', 'Mark and Acts', 'Matthew and Hebrews'],
    answer: 0,
    explanation:
      'Luke dedicates his account to ‘most excellent Theophilus\', and Acts opens by referring back to ‘the first book\'. Read together they run from Jesus\' birth to Paul preaching in Rome.',
    difficulty: 'medium',
    category: 'Books',
  },
  {
    id: 'q-obadiah-one-chapter',
    question:
      'Which Old Testament book is only a single chapter long?',
    options: ['Obadiah', 'Haggai', 'Joel', 'Nahum'],
    answer: 0,
    explanation:
      'Twenty-one verses of judgement on Edom, Israel\'s neighbour and kin through Esau. Four New Testament books are also single chapters: Philemon, 2 John, 3 John and Jude.',
    difficulty: 'hard',
    category: 'Books',
  },
  {
    id: 'q-apocalyptic-genre',
    question:
      'Revelation, like parts of Daniel and Ezekiel, belongs to a genre of visions, symbolic beasts and heavenly messengers. What is that genre called?',
    options: ['Apocalyptic literature', 'Wisdom literature', 'Historical narrative', 'Epistolary literature'],
    answer: 0,
    explanation:
      '‘Apocalypse\' simply means ‘unveiling\' — the Greek word with which the book opens. Revelation is also a letter, sent to seven named churches in what is now western Turkey, which is why it carries greetings much like Paul\'s.',
    difficulty: 'hard',
    category: 'Books',
  },
  {
    id: 'q-people-abraham-wife',
    question:
      'Which woman laughed when she overheard that she would bear a son in her old age?',
    options: ['Sarah', 'Rebekah', 'Rachel', 'Hannah'],
    answer: 0,
    explanation:
      'The laughter is stitched into the name: Isaac means roughly \'he laughs\'. Abraham had laughed at the same promise a chapter earlier, so the joke ran in the family before the child did.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-people-deborah-role',
    question:
      'Deborah, who summoned Barak to battle against Sisera, held which position in Israel?',
    options: ['Judge and prophetess', 'A prophetess, but not a judge', 'A judge, but never called a prophetess', 'Wife of Barak and his war counsellor'],
    answer: 0,
    explanation:
      'Judges 4:4 stacks the titles together: she is a prophetess, the wife of Lappidoth — not of Barak — and judging Israel at that time. She is also the only judge shown holding court, with Israelites coming up to her under a palm tree between Ramah and Bethel for rulings. Barak refused to march unless she came with him, and she warned him the glory would go to a woman.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-people-ruth-loyalty',
    question:
      'To whom did Ruth say, ‘where you go, I will go’?',
    options: ['Naomi, her mother-in-law', 'Boaz, her future husband', 'Orpah, her sister-in-law', 'Mahlon, her first husband'],
    answer: 0,
    explanation:
      'Ruth was a Moabite, from a people Israel had long regarded with suspicion, yet she becomes the great-grandmother of King David. Matthew\'s genealogy of Jesus names her without apology.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-people-esther-cousin',
    question:
      'Who raised Esther and urged her to plead with the king for the Jews?',
    options: ['Mordecai', 'Haman', 'Nehemiah', 'Ezra'],
    answer: 0,
    explanation:
      'Mordecai, her cousin and guardian, told her she may have come to the kingdom for such a time as this — the closest the book gets to naming God, who is never mentioned outright in the Hebrew text of Esther.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-people-mary-magdalene-tomb',
    question:
      'According to John\'s Gospel, who mistook the risen Jesus for the gardener?',
    options: ['Mary Magdalene', 'Martha of Bethany', 'Joanna', 'Salome'],
    answer: 0,
    explanation:
      'She was standing outside the tomb weeping and did not recognise him until he said her name — “Mary.” All four Gospels place women at the empty tomb before anyone else.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-people-priscilla-aquila',
    question:
      'Priscilla and Aquila took Apollos aside to explain the way of God more accurately to him. What trade did the couple share with Paul?',
    options: ['Tent-making', 'Dyeing cloth', 'Silversmithing', 'Fishing'],
    answer: 0,
    explanation:
      'Paul lodged and worked with them in Corinth after they were expelled from Rome. Priscilla is usually named first — unusual enough in ancient writing to suggest she was the more prominent teacher of the two.',
    difficulty: 'hard',
    category: 'People',
  },
  {
    id: 'q-people-lydia-purple',
    question:
      'Lydia, Paul\'s first recorded convert in Europe, dealt in which commodity?',
    options: ['Purple cloth', 'Olive oil', 'Grain', 'Frankincense'],
    answer: 0,
    explanation:
      'Purple cloth was a luxury trade. The most prized purple came from shellfish, though Lydia\'s home city of Thyatira was also known for a plant-based purple, and Acts does not say which she sold. What Acts does show is a woman with a household and a house large enough to host Paul\'s group: she and her household were baptised, and that house became the base of the church in Philippi.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-people-moses-brother',
    question:
      'Who spoke to Pharaoh on Moses\' behalf and later became Israel\'s first high priest?',
    options: ['Aaron', 'Joshua', 'Caleb', 'Eleazar'],
    answer: 0,
    explanation:
      'Moses protested that he was slow of speech, so Aaron became his mouthpiece. The same brother later gave in to the people and made the golden calf while Moses was up the mountain.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-people-samuel-anoint',
    question:
      'Which prophet anointed both Saul and David as king?',
    options: ['Samuel', 'Nathan', 'Elijah', 'Ahijah'],
    answer: 0,
    explanation:
      'When Samuel went to Jesse\'s house he assumed the tallest son was the choice, and was told that people look at the outward appearance while God looks at the heart. David, out with the sheep, had not even been called in.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-people-elijah-successor',
    question:
      'Who received Elijah\'s mantle and asked for a double portion of his spirit?',
    options: ['Elisha', 'Obadiah', 'Micaiah', 'Gehazi'],
    answer: 0,
    explanation:
      'Elisha was ploughing with twelve yoke of oxen when Elijah threw his mantle over him. He slaughtered his own pair, cooked the meat over the wooden plough gear and fed the workers — a farewell meal — then followed Elijah.',
    difficulty: 'medium',
    category: 'People',
  },
  {
    id: 'q-people-tax-collector',
    question:
      'Which disciple was working as a tax collector when Jesus called him?',
    options: ['Matthew', 'Thomas', 'Philip', 'Bartholomew'],
    answer: 0,
    explanation:
      'Tax collectors worked for the occupying power and were despised as collaborators. Jesus\' answer to the complaints was that the healthy have no need of a physician.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-people-paul-damascus',
    question:
      'Before his encounter on the Damascus road, Saul of Tarsus was best known for what?',
    options: ['Persecuting followers of Jesus', 'Collecting taxes for Rome', 'Serving as a temple musician', 'Commanding a Roman garrison'],
    answer: 0,
    explanation:
      'He had guarded the coats of those who stoned Stephen. Ananias, told to go and lay hands on him, argued back — the reputation had travelled ahead of the conversion.',
    difficulty: 'easy',
    category: 'People',
  },
  {
    id: 'q-people-barnabas-name',
    question:
      'Barnabas, who sold a field and laid the money at the apostles’ feet and later travelled with Paul on the first missionary journey, was given a nickname meaning what?',
    options: ['Son of Encouragement', 'Son of Thunder', 'Rock', 'Beloved Physician'],
    answer: 0,
    explanation:
      'His given name was Joseph, and he was a Levite from Cyprus; the apostles themselves gave him the new name. He was also the one who vouched for Saul when the Jerusalem believers were still afraid of him. He and Paul later parted company in a sharp disagreement over whether to take John Mark along — and Barnabas, true to the nickname, took the younger man.',
    difficulty: 'hard',
    category: 'People',
  },
  {
    id: 'q-people-hosea-marriage',
    question:
      'Which prophet was told to marry an unfaithful wife as a living picture of Israel\'s unfaithfulness?',
    options: ['Hosea', 'Amos', 'Habakkuk', 'Zephaniah'],
    answer: 0,
    explanation:
      'He named his children Jezreel, Lo-Ruhamah (‘not pitied’) and Lo-Ammi (‘not my people’). Chapter 2 reverses the last two into a promise: those who were not God\'s people are told “You are my people.”',
    difficulty: 'hard',
    category: 'People',
  },
  {
    id: 'q-verse-jesus-wept',
    question:
      'The shortest verse in most English Bibles reads simply “Jesus wept.” In which book does it appear?',
    options: ['John', 'Luke', 'Matthew', 'Mark'],
    answer: 0,
    explanation:
      'It falls at Lazarus’ grave in Bethany, moments before Jesus calls him out of it — grief and power in the same scene.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-verse-thomas-confession',
    question:
      'Who greeted the risen Jesus with the words “My Lord and my God!”?',
    options: ['Thomas', 'Philip', 'Nathanael', 'Peter'],
    answer: 0,
    explanation:
      'He had missed the earlier appearance and demanded proof; a week later he gets it, and the next verse blesses those who believe without seeing.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-verse-great-commission',
    question:
      '“Go and make disciples of all nations” — which book ends with this commission?',
    options: ['Matthew', 'Mark', 'Luke', 'John'],
    answer: 0,
    explanation:
      'The same closing paragraph promises “I am with you always, even to the end of the age”, echoing the name given in chapter one — Immanuel, God with us.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-verse-job-blessed-name',
    question:
      'Who said “Yahweh gave, and Yahweh has taken away. Blessed be Yahweh’s name”?',
    options: ['Job', 'Abraham', 'David', 'Jeremiah'],
    answer: 0,
    explanation:
      'He says it the day messengers arrive reporting the loss of his livestock, his servants and his ten children — before his friends turn up and the long argument begins.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-verse-season-time',
    question:
      '“For everything there is a season, and a time for every purpose under heaven” opens a famous poem in which book?',
    options: ['Ecclesiastes', 'Proverbs', 'Psalms', 'Job'],
    answer: 0,
    explanation:
      'The speaker calls himself the Preacher, and the lines that follow pair opposites — a time to be born, a time to die. The Byrds turned them into a hit single in 1965.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-verse-justice-rolls',
    question:
      'Which prophet wrote “But let justice roll on like rivers, and righteousness like a mighty stream”?',
    options: ['Amos', 'Hosea', 'Micah', 'Habakkuk'],
    answer: 0,
    explanation:
      'He was a herdsman from Tekoa rather than a career prophet, and his complaint was against a prosperous nation whose worship he says God will not accept while the poor are crushed. Martin Luther King Jr. borrowed the line for his speeches.',
    difficulty: 'hard',
    category: 'Verses',
  },
  {
    id: 'q-verse-prince-of-peace',
    question:
      'Isaiah names a promised child Wonderful Counselor, Mighty God, Everlasting Father — and what else?',
    options: ['Prince of Peace', 'King of Kings', 'Lord of Hosts', 'Holy One of Israel'],
    answer: 0,
    explanation:
      'Handel set the whole string of titles in Messiah. “King of kings” is a New Testament title, used of Christ in Revelation and of God in 1 Timothy, while “the Holy One of Israel” is Isaiah’s favourite name for God — he uses it about two dozen times.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-verse-all-things-good',
    question:
      'In which letter do we read “We know that all things work together for good for those who love God”?',
    options: ['Romans', 'Galatians', 'Ephesians', 'Philippians'],
    answer: 0,
    explanation:
      'The chapter opens with “There is therefore now no condemnation” and closes with a list of things that cannot separate us from the love of God, so the verse sits in the middle of a sustained argument about God\'s purpose.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-verse-bread-alone',
    question:
      'Tempted in the wilderness, Jesus replies “Man shall not live by bread alone.” Which book is he quoting?',
    options: ['Deuteronomy', 'Exodus', 'Leviticus', 'Numbers'],
    answer: 0,
    explanation:
      'All three of his answers to the tempter come from that one book — words Moses spoke about Israel’s forty years in the desert, now matched to forty days.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-verse-send-me',
    question:
      'Who answered a voice asking “Whom shall I send?” with “Here I am. Send me!”?',
    options: ['Isaiah', 'Samuel', 'Jeremiah', 'Ezekiel'],
    answer: 0,
    explanation:
      'The call follows a temple vision in the year King Uzziah died, with a live coal touching his lips. Samuel’s famous night-time reply was a different one — “Speak, for your servant hears.”',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-verse-door-knock',
    question:
      '“Behold, I stand at the door and knock” closes a letter to which of the seven churches in Revelation?',
    options: ['Laodicea', 'Sardis', 'Ephesus', 'Philadelphia'],
    answer: 0,
    explanation:
      'It is the church called neither cold nor hot, and rich in its own eyes — which makes the image sharper: Christ is knocking from outside his own congregation.',
    difficulty: 'hard',
    category: 'Verses',
  },
  {
    id: 'q-verse-peacemakers',
    question:
      '“Blessed are the peacemakers, for they shall be called …” — how does the line finish?',
    options: ['children of God', 'the salt of the earth', 'heirs of the Kingdom', 'sons of Abraham'],
    answer: 0,
    explanation:
      'It belongs to the run of blessings that opens the Sermon on the Mount, each naming a present condition and the reward that answers it. “You are the salt of the earth” arrives a few verses later.',
    difficulty: 'easy',
    category: 'Verses',
  },
  {
    id: 'q-verse-new-every-morning',
    question:
      '“They are new every morning. Great is your faithfulness.” Which book are these lines from?',
    options: ['Lamentations', 'Jeremiah', 'Isaiah', 'Hosea'],
    answer: 0,
    explanation:
      'The book is five poems mourning Jerusalem’s fall to Babylon, and this flash of hope sits in the middle one. The hymn “Great Is Thy Faithfulness” grew out of it.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-verse-clean-heart',
    question:
      'Psalm 51, which prays “Create in me a clean heart, O God”, carries a heading naming which king?',
    options: ['David', 'Solomon', 'Hezekiah', 'Josiah'],
    answer: 0,
    explanation:
      'The heading ties the psalm to Nathan the prophet’s confrontation over Bathsheba — which is why it pleads for a washed conscience rather than rescue from enemies.',
    difficulty: 'medium',
    category: 'Verses',
  },
  {
    id: 'q-bethlehem-birth',
    question:
      'In which town was Jesus born?',
    options: ['Bethlehem', 'Nazareth', 'Jerusalem', 'Capernaum'],
    answer: 0,
    explanation:
      'Bethlehem was David’s home town, which is why Luke calls it “the city of David” — and centuries earlier Micah had pointed to it: “But you, Bethlehem Ephrathah, being small among the clans of Judah, out of you one will come out to me that is to be ruler in Israel.”',
    difficulty: 'easy',
    category: 'Places',
  },
  {
    id: 'q-nazareth-hometown',
    question:
      'In which Galilean town did Jesus grow up?',
    options: ['Nazareth', 'Cana', 'Capernaum', 'Chorazin'],
    answer: 0,
    explanation:
      'Nazareth was a small, unremarkable place, which is the point of Nathanael’s blunt question in John’s Gospel: “Can any good thing come out of Nazareth?” The name stuck to Jesus for life — the charge nailed above the cross named him as the Nazarene.',
    difficulty: 'easy',
    category: 'Places',
  },
  {
    id: 'q-egypt-flight',
    question:
      'According to Matthew, where did Joseph take Mary and the infant Jesus to escape Herod?',
    options: ['Egypt', 'Assyria', 'Arabia', 'Moab'],
    answer: 0,
    explanation:
      'Egypt was the obvious refuge — close, outside Herod’s reach, and home to a large Jewish population. Matthew then quotes Hosea, “Out of Egypt I called my son,” deliberately echoing Israel’s own escape from slavery there.',
    difficulty: 'easy',
    category: 'Places',
  },
  {
    id: 'q-galilee-sea',
    question:
      'Beside which body of water did Jesus call Simon Peter, Andrew, James and John to follow him?',
    options: ['The Sea of Galilee', 'The Jordan River', 'The Dead Sea', 'The Mediterranean Sea'],
    answer: 0,
    explanation:
      'It is really a freshwater lake, about thirteen miles long and roughly 200 metres below sea level, also called Gennesaret and Tiberias. Cold air funnelling down the surrounding hills can whip it into sudden squalls — the kind of storm the Gospels describe swamping the disciples’ boat.',
    difficulty: 'easy',
    category: 'Places',
  },
  {
    id: 'q-patmos-revelation',
    question:
      'On which island did John receive the visions recorded in the book of Revelation?',
    options: ['Patmos', 'Cyprus', 'Crete', 'Malta'],
    answer: 0,
    explanation:
      'Patmos is a rocky Aegean island off the coast of what is now Turkey; John says he was there “because of God’s Word and the testimony of Jesus Christ”, and early Christian tradition took that to mean banishment. The other islands have their own New Testament stories — Barnabas came from Cyprus, Titus was left in Crete, and Paul was shipwrecked on Malta.',
    difficulty: 'medium',
    category: 'Places',
  },
  {
    id: 'q-antioch-christians',
    question:
      'In which city were the followers of Jesus first called Christians?',
    options: ['Antioch', 'Jerusalem', 'Ephesus', 'Corinth'],
    answer: 0,
    explanation:
      'This was Antioch in Syria — not the Pisidian Antioch Paul later visited — a huge, cosmopolitan Roman city where Jews and Gentiles worshipped together in one church. That mixed congregation became the launch pad for Paul’s missionary journeys.',
    difficulty: 'medium',
    category: 'Places',
  },
  {
    id: 'q-damascus-road',
    question:
      'Saul of Tarsus was on the road to which city when a light from heaven stopped him?',
    options: ['Damascus', 'Caesarea', 'Joppa', 'Antioch'],
    answer: 0,
    explanation:
      'He was carrying letters authorising him to arrest followers of “the Way” who had fled there. Damascus became the place of his baptism at the hands of a nervous believer named Ananias — and, soon after, of his undignified escape over the city wall in a basket.',
    difficulty: 'medium',
    category: 'Places',
  },
  {
    id: 'q-babylon-exile',
    question:
      'Complete the opening of Psalm 137: “By the rivers of ———, there we sat down. Yes, we wept, when we remembered Zion.”',
    options: ['Babylon', 'Egypt', 'Assyria', 'Edom'],
    answer: 0,
    explanation:
      'The psalm is sung by exiles deported after Nebuchadnezzar destroyed Jerusalem and its temple in 586 BC. The Persian king Cyrus later allowed them home, but the name stuck as shorthand for proud, godless empire — which is how Revelation still uses it.',
    difficulty: 'medium',
    category: 'Places',
  },
  {
    id: 'q-horeb-elijah',
    question:
      'Fleeing Queen Jezebel, Elijah walked forty days to which mountain, where he heard “a still small voice”?',
    options: ['Horeb', 'Carmel', 'Nebo', 'Hermon'],
    answer: 0,
    explanation:
      'Horeb is the mountain’s other name — Deuteronomy and Kings prefer ‘Horeb’, while Exodus usually says ‘Sinai’ for the giving of the law, though Exodus calls the burning-bush mountain Horeb too (Exodus 3:1). Elijah’s forty-day trek there deliberately retraces Israel’s founding journey — and the answer he gets is a whisper rather than the thunder Moses knew.',
    difficulty: 'medium',
    category: 'Places',
  },
  {
    id: 'q-moriah-temple',
    question:
      'Second Chronicles says Solomon built the temple in Jerusalem on which hill?',
    options: ['Mount Moriah', 'Mount Tabor', 'Mount Gilboa', 'Mount Ebal'],
    answer: 0,
    explanation:
      'The same passage identifies the spot as the threshing floor David had bought from Ornan the Jebusite, so the temple stood on ground paid for rather than seized. Genesis places Abraham’s near-sacrifice of Isaac in “the land of Moriah”, a connection Jewish tradition has long drawn to this hill.',
    difficulty: 'hard',
    category: 'Places',
  },
  {
    id: 'q-grace-meaning',
    question:
      'In Christian teaching, the word ‘grace’ refers chiefly to what?',
    options: ['God’s favour given freely, not earned', 'God’s reward for a life of good deeds', 'The self-discipline of a devout believer', 'The gratitude a believer owes to God'],
    answer: 0,
    explanation:
      'The Greek word is charis, which also lies behind ‘charisma’ — a gift handed over, not a wage owed. Paul sharpens the contrast in Romans 4, where he notes that a worker’s pay is not counted as a favour but as a debt.',
    difficulty: 'easy',
    category: 'Doctrine',
  },
  {
    id: 'q-first-day-week',
    question:
      'According to the Gospels, when was Jesus’ tomb found empty?',
    options: ['On the first day of the week', 'On the seventh day of the week', 'On the evening before the Sabbath', 'On the last day of the Passover'],
    answer: 0,
    explanation:
      'That timing is why most Christian traditions came to gather on the first day of the week. Revelation opens with John ‘in the Spirit on the Lord’s day’ (1:10) — a phrase early writers connected with the day of the resurrection, though the verse does not name a weekday.',
    difficulty: 'easy',
    category: 'Doctrine',
  },
  {
    id: 'q-trinity-formula',
    question:
      'Matthew 28:19, the risen Jesus’ command to baptise new disciples, is most often cited in discussions of which doctrine?',
    options: ['The Trinity', 'The incarnation', 'The atonement', 'The last judgement'],
    answer: 0,
    explanation:
      'The verse ends Matthew’s Gospel with disciples baptised ‘in the name of the Father and of the Son and of the Holy Spirit’ — a single name shared by three, which is why it became the standard proof text. The word ‘Trinity’ appears nowhere in the Bible; it was coined later as shorthand for holding together the scattered things scripture says about Father, Son and Spirit. Paul’s blessing at the close of 2 Corinthians is the other main three-part text.',
    difficulty: 'easy',
    category: 'Doctrine',
  },
  {
    id: 'q-supper-passover',
    question:
      'At which meal did Jesus take bread and a cup and tell his disciples to do this in remembrance of him?',
    options: ['A Passover meal in an upstairs room', 'A wedding feast at Cana in Galilee', 'A breakfast beside the Sea of Galilee', 'A dinner at Bethany with Lazarus'],
    answer: 0,
    explanation:
      'Paul repeats the words in 1 Corinthians 11 and says he is passing on what he himself received — a sign the wording was already fixed and in use very early. The Greek for the thanks Jesus gave over the bread, eucharistia, became one of the meal’s oldest names.',
    difficulty: 'easy',
    category: 'Doctrine',
  },
  {
    id: 'q-ekklesia',
    question:
      'The New Testament word translated ‘church’ — ekklesia — originally meant what?',
    options: ['An assembly of people called together', 'A building set apart for worship', 'A council of religious teachers', 'A festival held once each year'],
    answer: 0,
    explanation:
      'In ordinary Greek it named a town’s citizens summoned to meet, which is why it can describe a handful of people in someone’s living room. The World English Bible often renders it simply ‘assembly’ — Paul greets ‘the assembly that is in their house’ in Romans 16.',
    difficulty: 'easy',
    category: 'Doctrine',
  },
  {
    id: 'q-abraham-credited',
    question:
      'Paul builds his case that righteousness comes through faith on a line from Genesis: this man believed God, who ‘credited it to him for righteousness’. Who was he?',
    options: ['Abraham', 'Moses', 'David', 'Job'],
    answer: 0,
    explanation:
      'Paul’s point is that the verdict came before circumcision and centuries before the law was given at Sinai. James reaches for the same man from another angle, pointing to a trust that showed itself in what he did.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-baptism-romans-6',
    question:
      'In Romans 6, Paul says that those baptised into Christ are joined to what?',
    options: ['His death and burial, and so his risen life', 'His anointing by the Spirit at the Jordan', 'His forty days of fasting in the wilderness', 'His authority to forgive sins on earth'],
    answer: 0,
    explanation:
      'The image is a going down and a coming up — which is why Paul can treat baptism as an argument for how to live, not just a rite. Christians baptise in different ways today, by immersion, pouring or sprinkling, but that shape lies behind them all.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-resurrection-corinthians',
    question:
      'Which letter contains Paul’s extended defence of the resurrection, including the blunt line that if Christ has not been raised, ‘your faith is vain’?',
    options: ['1 Corinthians', 'Romans', 'Hebrews', '1 Peter'],
    answer: 0,
    explanation:
      'Paul goes on to call Christ ‘the first fruits of those who are asleep’ — a harvest metaphor, in which the first sheaf guarantees the rest of the crop. The resurrection, on that reading, is not a one-off escape but an opening.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-pentecost-acts',
    question:
      'The coming of the Holy Spirit on the gathered believers at Pentecost is told in which book?',
    options: ['Acts', 'Luke', 'John', 'Hebrews'],
    answer: 0,
    explanation:
      'Pentecost was already a Jewish harvest festival — its name comes from the Greek for ‘fiftieth’, counted from Passover. The city was full of pilgrims from across the empire, which is why the crowd could each hear their own language.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-isaiah-servant',
    question:
      'Which prophetic book contains the ‘suffering servant’ poem — ‘he was pierced for our transgressions’ — that New Testament writers read as pointing to Christ?',
    options: ['Isaiah', 'Jeremiah', 'Ezekiel', 'Hosea'],
    answer: 0,
    explanation:
      'Acts 8 stages the connection directly: an Ethiopian official is reading this very passage aloud in his chariot and asks who the prophet is speaking about. Philip starts there and works forwards.',
    difficulty: 'medium',
    category: 'Doctrine',
  },
  {
    id: 'q-kenosis-hymn',
    question:
      'A famous passage in one of Paul’s letters says that Christ ‘emptied himself, taking the form of a servant’. Which letter?',
    options: ['Philippians', 'Colossians', 'Ephesians', 'Galatians'],
    answer: 0,
    explanation:
      'The Greek verb behind ‘emptied’ gives theologians the term kenosis. Many scholars think Paul is quoting an early Christian hymn here, though others argue he composed the lines himself. Either way, he introduces the passage not to settle an argument about Christ’s nature but to press a small congregation to stop looking out only for themselves.',
    difficulty: 'hard',
    category: 'Doctrine',
  },
  {
    id: 'q-nicaea-creed',
    question:
      'The creed recited across the Christian world, affirming the Son as ‘of one substance with the Father’, takes its name from which city?',
    options: ['Nicaea', 'Antioch', 'Alexandria', 'Ephesus'],
    answer: 0,
    explanation:
      'Nicaea is modern İznik in Turkey, where bishops met in 325. The version most congregations say aloud reflects a later gathering at Constantinople in 381, which filled out the lines about the Holy Spirit.',
    difficulty: 'hard',
    category: 'Doctrine',
  },
  {
    id: 'q-story-flood-covenant',
    question:
      'After the flood, God sets a rainbow in the clouds. What does the narrative say it is for?',
    options: ['A sign of God\'s promise never again to destroy the earth by flood', 'A reward for Noah\'s obedience in building the ark', 'A warning to future generations to avoid Noah\'s sins', 'A marker of the boundary between the waters and dry land'],
    answer: 0,
    explanation:
      'The Hebrew word is simply ‘bow’ — the ordinary word for a warrior’s weapon — now hung up in the clouds. And the covenant is made not just with Noah but with ‘every living creature’.',
    difficulty: 'easy',
    category: 'The Story',
  },
  {
    id: 'q-story-abraham-call',
    question:
      'God’s call of Abraham in Genesis 12 promises him land and descendants. What does the same promise say about the other nations?',
    options: ['That all the families of the earth will be blessed through him', 'That they will be driven out to make room for his descendants', 'That they will bring tribute to his descendants’ king', 'That they will be judged for refusing to worship his God'],
    answer: 0,
    explanation:
      'This clause turns a family story into a world story, and it is the thread the New Testament picks up when Paul argues that the promise to Abraham always had the nations in view.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-passover-cause',
    question:
      'Why did Israel\'s families smear blood on their doorframes on the night of the exodus?',
    options: ['So the destroyer would pass over their houses during the final plague', 'So the Egyptians would know which houses to leave unlooted', 'So the angel would know which families to lead out first', 'So the firstborn sons could be dedicated to God\'s service'],
    answer: 0,
    explanation:
      'The meal eaten that night — roasted lamb, bitter herbs and unleavened bread — became the annual Passover; the dough they carried out of Egypt had no time to rise either. In the synoptic Gospels it is the festival Jesus is keeping at the Last Supper.',
    difficulty: 'easy',
    category: 'The Story',
  },
  {
    id: 'q-story-forty-years',
    question:
      'Why did Israel spend forty years in the wilderness rather than entering Canaan directly?',
    options: ['The generation that refused to enter after the spies\' report had to die out first', 'The tribes needed time to grow numerous enough to hold the land', 'Moses was waiting for the Canaanite kingdoms to weaken through war', 'The law given at Sinai took forty years to teach to the whole nation'],
    answer: 0,
    explanation:
      'Ten of the twelve scouts said the land could not be taken; only Caleb and Joshua disagreed, and they were the only two of that generation permitted to cross the Jordan.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-judges-cycle',
    question:
      'The book of Judges repeats a pattern. What is its usual order?',
    options: ['Israel turns away, is oppressed, cries out, and is rescued by a judge', 'A judge dies, a king is crowned, the king fails, and a judge returns', 'Israel wins a battle, grows proud, is warned by a prophet, and repents', 'A famine strikes, Israel migrates, is enslaved abroad, and returns home'],
    answer: 0,
    explanation:
      'The cycle spirals downward rather than simply repeating — the judges themselves grow more compromised as the book goes on, and it ends with the bleak refrain that everyone did what was right in his own eyes.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-king-request',
    question:
      'When Israel demanded a king, what reason did they give?',
    options: ['They wanted to be like all the surrounding nations', 'They wanted an heir to Moses to lead them in battle', 'They wanted someone to rebuild the tabernacle as a temple', 'They wanted a ruler who could unite the twelve tribes\' languages'],
    answer: 0,
    explanation:
      'Samuel took it as a personal rejection, but God told him the people were rejecting God as king. Samuel then warned them exactly what a king would cost them in sons, daughters, fields and taxes.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-kingdom-split',
    question:
      'The kingdom split in two after Solomon\'s death. What triggered the break?',
    options: ['Rehoboam refused to lighten the heavy labour and taxes his father had imposed', 'Rehoboam was too young to be crowned and a regent seized the north', 'Jeroboam won a battle against Egypt and claimed the throne by right', 'The northern tribes objected to the temple being built in Jerusalem'],
    answer: 0,
    explanation:
      'Rehoboam took the advice of his contemporaries over that of his father\'s elder counsellors. Ten tribes went with Jeroboam, leaving the house of David ruling only Judah and Benjamin.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-exile-return',
    question:
      'The exiles returned to Jerusalem under Persian rule rather than Babylonian. Why?',
    options: ['Persia conquered Babylon and reversed its policy of deporting captive peoples', 'Babylon collapsed into civil war and the exiles escaped in the confusion', 'Babylon sold the exiles\' freedom to Persia in exchange for a treaty', 'Persia captured Jerusalem and resettled it with the exiles as a garrison'],
    answer: 0,
    explanation:
      'Cyrus of Persia preferred to return displaced peoples and their gods to their homelands, reckoning that grateful subjects rebel less. Isaiah startlingly calls him God\'s \'anointed\' — the word we usually render \'messiah\'.',
    difficulty: 'hard',
    category: 'The Story',
  },
  {
    id: 'q-story-exile-cause',
    question:
      'How do the books of Kings and the prophets explain why Judah went into exile?',
    options: ['Persistent idolatry and injustice, despite repeated warnings from prophets', 'Improper temple worship, with sacrifices offered by non-Aaronic priests', 'The departure of the glory when the ark was captured by the Philistines', 'The loss of the book of the law, leaving the covenant unknown to the people'],
    answer: 0,
    explanation:
      'The prophets treat the exile as covenant consequence rather than divine defeat — which is why the same books can promise return. Jeremiah even names the length: seventy years.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-temple-curtain',
    question:
      'The gospels record that the temple curtain tore in two when Jesus died. How does the New Testament go on to interpret access to God after the cross?',
    options: ['That the way into God\'s presence was now open', 'That the temple would be rebuilt within three days', 'That the priests had failed in their duties that day', 'That the old covenant sacrifices had been proved fraudulent'],
    answer: 0,
    explanation:
      'The curtain screened the Most Holy Place, which only the high priest entered, and only once a year. Matthew and Mark add that it tore from top to bottom. The gospels report the tearing without explaining it: the reading that the way into God\'s presence is now open comes from Hebrews 10:19-20, while many readers of Mark also hear in it a warning of judgement on the temple.',
    difficulty: 'easy',
    category: 'The Story',
  },
  {
    id: 'q-story-pentecost-effect',
    question:
      'At Pentecost the Spirit came on the disciples. What immediately followed?',
    options: ['They spoke in the languages of the pilgrims gathered from many nations', 'They were arrested and brought before the Roman governor', 'They divided the known world between them and left Jerusalem', 'They elected seven deacons to manage the community\'s food'],
    answer: 0,
    explanation:
      'The scene reads as Babel run backwards: there, one language scattered into many; here, many languages gather a crowd into one hearing. Peter\'s sermon that day is the church\'s first.',
    difficulty: 'medium',
    category: 'The Story',
  },
  {
    id: 'q-story-gentile-question',
    question:
      'The council at Jerusalem in Acts 15 settled which question?',
    options: ['Whether Gentile believers had to be circumcised and keep the law of Moses', 'Whether Gentile believers could be baptised without a Jewish sponsor', 'Whether the apostles could appoint elders in churches outside Judea', 'Whether believers should sell their property and share the proceeds'],
    answer: 0,
    explanation:
      'The decision — that they need not — is why the movement spread as a faith for anyone rather than a sect within Judaism. Peter\'s argument rested on what he had already seen happen at Cornelius\'s house.',
    difficulty: 'hard',
    category: 'The Story',
  },
];

export const QUESTION_CATEGORIES = [
  ...new Set(QUESTIONS.map((q) => q.category)),
].sort();

/** XP awarded for a correct answer, weighted by difficulty. */
export const XP_BY_DIFFICULTY = { easy: 8, medium: 12, hard: 18 };
